import React, { useState, useEffect } from 'react';
import {
  TrendingUp, TrendingDown, DollarSign, AlertCircle, CheckCircle,
  Clock, Zap, Shield, PlayCircle, RefreshCw, Download, Filter
} from 'lucide-react';
import { getGoogleAdsClient, getDateRange } from '@/lib/google-ads-client';
import { createGoogleAdsAIOptimizer, type BidRecommendation } from '@/lib/google-ads-ai-optimizer';
import { supabase } from '@/lib/supabase';
import toast from 'react-hot-toast';
import { useGoogleAdsCompany, GOOGLE_ADS_COMPANY_OPTIONS } from '@/hooks/useGoogleAdsCompany';

interface BidRecommendationWithStatus extends BidRecommendation {
  selected: boolean;
  status?: 'pending' | 'applied' | 'error';
  appliedAt?: string;
}

export default function AutomatedBidManagerPage() {
  const [recommendations, setRecommendations] = useState<BidRecommendationWithStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [minConfidence, setMinConfidence] = useState(0.75);
  const [maxBidChange, setMaxBidChange] = useState(0.5);
  const [dryRun, setDryRun] = useState(true);
  const { company, setCompany } = useGoogleAdsCompany();

  useEffect(() => {
    loadRecommendations();
  }, [company]);

  const loadRecommendations = async () => {
    try {
      setLoading(true);

      // Get customer ID
      const { data: mappings } = await supabase
        .from('google_ads_customer_mapping')
        .select('*')
        .eq('company', company)
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();

      if (!mappings) {
        toast.error('No Google Ads account connected');
        return;
      }

      const customerId = mappings.customer_id;
      const client = getGoogleAdsClient(customerId);
      const optimizer = createGoogleAdsAIOptimizer(client);

      // Generate optimization report
      const range = getDateRange(30);
      const report = await optimizer.generateOptimizationReport(customerId, range);

      // Convert to recommendations with status
      const recsWithStatus: BidRecommendationWithStatus[] = report.bidRecommendations.map(rec => ({
        ...rec,
        selected: rec.confidence >= minConfidence && rec.priority === 'high',
      }));

      setRecommendations(recsWithStatus);

    } catch (error) {
      console.error('Error loading recommendations:', error);
      toast.error('Failed to load bid recommendations');
    } finally {
      setLoading(false);
    }
  };

  const toggleRecommendation = (keywordId: string) => {
    setRecommendations(prev =>
      prev.map(rec =>
        rec.keywordId === keywordId ? { ...rec, selected: !rec.selected } : rec
      )
    );
  };

  const toggleAll = () => {
    const filteredRecs = getFilteredRecommendations();
    const allSelected = filteredRecs.every(rec => rec.selected);
    
    setRecommendations(prev =>
      prev.map(rec => {
        if (filteredRecs.find(fr => fr.keywordId === rec.keywordId)) {
          return { ...rec, selected: !allSelected };
        }
        return rec;
      })
    );
  };

  const applyRecommendations = async () => {
    const selected = recommendations.filter(rec => rec.selected);
    
    if (selected.length === 0) {
      toast.error('No recommendations selected');
      return;
    }

    if (dryRun) {
      toast.success(`Dry run: Would apply ${selected.length} bid changes`, {
        duration: 5000,
      });
      return;
    }

    try {
      setApplying(true);

      const { data: mappings } = await supabase
        .from('google_ads_customer_mapping')
        .select('customer_id')
        .eq('company', company)
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();

      if (!mappings) return;

      const client = getGoogleAdsClient(mappings.customer_id);
      const optimizer = createGoogleAdsAIOptimizer(client);

      // Apply recommendations
      const result = await optimizer.applyBidRecommendations(
        mappings.customer_id,
        selected,
        {
          minConfidence,
          maxBidChange,
          dryRun: false,
        }
      );

      // Update status
      setRecommendations(prev =>
        prev.map(rec => {
          if (rec.selected) {
            return {
              ...rec,
              status: 'applied',
              appliedAt: new Date().toISOString(),
            };
          }
          return rec;
        })
      );

      toast.success(
        `Applied ${result.applied} bid changes. Skipped ${result.skipped}.`,
        { duration: 5000 }
      );

      if (result.errors.length > 0) {
        console.error('Errors during application:', result.errors);
        toast.error(`${result.errors.length} errors occurred`, {
          duration: 7000,
        });
      }

    } catch (error) {
      console.error('Error applying recommendations:', error);
      toast.error('Failed to apply bid changes');
    } finally {
      setApplying(false);
    }
  };

  const getFilteredRecommendations = () => {
    return recommendations.filter(rec => {
      if (filterPriority !== 'all' && rec.priority !== filterPriority) {
        return false;
      }
      return true;
    });
  };

  const selectedCount = recommendations.filter(rec => rec.selected).length;
  const filteredRecs = getFilteredRecommendations();
  
  const estimatedImpact = recommendations
    .filter(rec => rec.selected)
    .reduce((sum, rec) => sum + (rec.recommendedBid - rec.currentBid), 0);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
          <span className="ml-3 text-lg">Loading recommendations...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex justify-end mb-6">
        <select
          value={company}
          onChange={(e) => setCompany(e.target.value as 'hales' | 'myccs' | 'abraham')}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          {GOOGLE_ADS_COMPANY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Automated Bid Manager</h1>
        <p className="text-gray-600">AI-powered bid recommendations with one-click application</p>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Safety Controls</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Min Confidence */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Minimum Confidence
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={minConfidence}
                onChange={(e) => setMinConfidence(parseFloat(e.target.value))}
                className="w-full"
              />
              <div className="flex justify-between text-sm text-gray-600 mt-1">
                <span>0%</span>
                <span className="font-semibold">{(minConfidence * 100).toFixed(0)}%</span>
                <span>100%</span>
              </div>
            </div>

            {/* Max Bid Change */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Maximum Bid Change
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={maxBidChange}
                onChange={(e) => setMaxBidChange(parseFloat(e.target.value))}
                className="w-full"
              />
              <div className="flex justify-between text-sm text-gray-600 mt-1">
                <span>0%</span>
                <span className="font-semibold">{(maxBidChange * 100).toFixed(0)}%</span>
                <span>100%</span>
              </div>
            </div>

            {/* Dry Run Toggle */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Application Mode
              </label>
              <div className="flex items-center gap-3 mt-3">
                <button
                  onClick={() => setDryRun(!dryRun)}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                    dryRun
                      ? 'bg-yellow-100 text-yellow-800 border-2 border-yellow-300'
                      : 'bg-red-100 text-red-800 border-2 border-red-300'
                  }`}
                >
                  {dryRun ? '🧪 Dry Run (Safe)' : '⚡ Live Mode'}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {dryRun 
                  ? 'Test mode - no actual changes will be made'
                  : 'Live mode - changes will be applied to Google Ads'
                }
              </p>
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="p-6 bg-gray-50">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="text-sm text-gray-600">Total Recommendations</div>
              <div className="text-2xl font-bold text-gray-900">{recommendations.length}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Selected</div>
              <div className="text-2xl font-bold text-blue-600">{selectedCount}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Estimated Impact</div>
              <div className="text-2xl font-bold text-green-600">
                {estimatedImpact > 0 ? '+' : ''}{formatCurrency(estimatedImpact)}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-600">High Priority</div>
              <div className="text-2xl font-bold text-orange-600">
                {recommendations.filter(r => r.priority === 'high').length}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Actions Bar */}
      <div className="bg-white rounded-lg shadow p-4 mb-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={toggleAll}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium"
          >
            {filteredRecs.every(rec => rec.selected) ? 'Deselect All' : 'Select All'}
          </button>

          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="all">All Priorities</option>
            <option value="high">High Priority</option>
            <option value="medium">Medium Priority</option>
            <option value="low">Low Priority</option>
          </select>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={loadRecommendations}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>

          <button
            onClick={applyRecommendations}
            disabled={selectedCount === 0 || applying}
            className={`px-6 py-2 rounded-lg font-semibold flex items-center gap-2 ${
              selectedCount === 0 || applying
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : dryRun
                ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            {applying ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                Applying...
              </>
            ) : (
              <>
                <PlayCircle className="h-4 w-4" />
                {dryRun ? 'Test Changes' : `Apply ${selectedCount} Changes`}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Safety Warning */}
      {!dryRun && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-900">Live Mode Active</h3>
              <p className="text-sm text-red-700 mt-1">
                Changes will be immediately applied to your Google Ads account. Make sure you've reviewed all selected recommendations.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Recommendations List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Bid Recommendations ({filteredRecs.length})
          </h2>
        </div>

        <div className="divide-y divide-gray-200">
          {filteredRecs.map((rec) => {
            const bidChangePercent = ((rec.recommendedBid - rec.currentBid) / rec.currentBid) * 100;
            const isIncrease = rec.recommendedBid > rec.currentBid;

            return (
              <div
                key={rec.keywordId}
                className={`p-6 transition-colors ${rec.selected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
              >
                <div className="flex items-start gap-4">
                  {/* Checkbox */}
                  <input
                    type="checkbox"
                    checked={rec.selected}
                    onChange={() => toggleRecommendation(rec.keywordId)}
                    className="mt-1 h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />

                  {/* Content */}
                  <div className="flex-1">
                    {/* Keyword & Priority */}
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{rec.keywordText}</h3>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        rec.priority === 'high'
                          ? 'bg-red-100 text-red-800'
                          : rec.priority === 'medium'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {rec.priority.toUpperCase()}
                      </span>
                      <span className="text-sm text-gray-500">
                        {(rec.confidence * 100).toFixed(0)}% confidence
                      </span>
                    </div>

                    {/* Bid Change Info */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                      <div>
                        <div className="text-xs text-gray-500">Current Bid</div>
                        <div className="text-sm font-semibold">{formatCurrency(rec.currentBid)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Recommended Bid</div>
                        <div className="text-sm font-semibold flex items-center gap-2">
                          {formatCurrency(rec.recommendedBid)}
                          {isIncrease ? (
                            <TrendingUp className="h-4 w-4 text-green-600" />
                          ) : (
                            <TrendingDown className="h-4 w-4 text-red-600" />
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Change</div>
                        <div className={`text-sm font-semibold ${isIncrease ? 'text-green-600' : 'text-red-600'}`}>
                          {isIncrease ? '+' : ''}{bidChangePercent.toFixed(1)}%
                        </div>
                      </div>
                    </div>

                    {/* Reason */}
                    <p className="text-sm text-gray-700 mb-3">{rec.reason}</p>

                    {/* Expected Impact */}
                    <div className="flex items-center gap-6 text-xs">
                      <span className="text-gray-600">
                        Impressions: <span className="font-semibold">{rec.expectedImpact.impressions}</span>
                      </span>
                      <span className="text-gray-600">
                        Clicks: <span className="font-semibold">{rec.expectedImpact.clicks}</span>
                      </span>
                      <span className="text-gray-600">
                        Conversions: <span className="font-semibold">{rec.expectedImpact.conversions}</span>
                      </span>
                    </div>

                    {/* Applied Status */}
                    {rec.status === 'applied' && (
                      <div className="mt-3 flex items-center gap-2 text-green-600 text-sm">
                        <CheckCircle className="h-4 w-4" />
                        <span>Applied {rec.appliedAt && `on ${new Date(rec.appliedAt).toLocaleDateString()}`}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {filteredRecs.length === 0 && (
          <div className="p-12 text-center text-gray-500">
            <Zap className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p>No recommendations match current filters</p>
          </div>
        )}
      </div>

      {/* Safety Info Panel */}
      <div className="mt-6 bg-blue-50 rounded-lg p-6 border border-blue-200">
        <div className="flex items-start gap-3">
          <Shield className="h-6 w-6 text-blue-600 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-blue-900 mb-2">Safety Features Active</h3>
            <ul className="space-y-1 text-sm text-blue-800">
              <li>• Minimum confidence threshold: {(minConfidence * 100).toFixed(0)}%</li>
              <li>• Maximum bid change limit: {(maxBidChange * 100).toFixed(0)}%</li>
              <li>• {dryRun ? 'Dry-run mode enabled - no changes will be applied' : 'Live mode - changes will be applied immediately'}</li>
              <li>• All changes are logged and can be reviewed in bid change history</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
